<template>
  <div class="tab-panel-body">
    <!-- Header -->
    <div class="config-section-header">
      <div class="config-section-header__left">
        <v-icon small class="mr-2" color="text_secondary">mdi-monitor</v-icon>
        <span class="config-section-label">Tela de exibição</span>
      </div>
    </div>

    <!-- Settings -->
    <div class="sc-settings-list">
      <!-- Dark mode toggle -->
      <div class="sc-settings-list__row">
        <div class="sc-settings-list__info">
          <v-icon small color="text_secondary" class="mr-2">mdi-theme-light-dark</v-icon>
          <div class="sc-settings-list__text">
            <span class="sc-settings-list__name">Modo do card</span>
            <span class="sc-settings-list__desc">Alternar entre modo claro e escuro</span>
          </div>
        </div>
        <v-switch
          v-model="isDarkMode"
          :label="isDarkMode ? 'Escuro' : 'Claro'"
          @change="toggleDarkMode"
          color="primary"
          hide-details
          dense
          class="mt-0 pt-0"
        />
      </div>

      <!-- Card color preview (read-only) -->
      <div class="sc-settings-list__row">
        <div class="sc-settings-list__info">
          <v-icon small color="text_secondary" class="mr-2">mdi-card-text</v-icon>
          <div class="sc-settings-list__text">
            <span class="sc-settings-list__name">Cor do card de Notícias</span>
            <span class="sc-settings-list__desc">Definida automaticamente pelo modo claro/escuro</span>
          </div>
        </div>
        <div class="exibicao-color-preview-static">
          <div
            class="color-preview"
            :style="{ backgroundColor: currentCardColor }"
          ></div>
          <span class="exibicao-color-hex">{{ currentCardColor }}</span>
        </div>
      </div>

      <!-- Background color -->
      <div class="sc-settings-list__row">
        <div class="sc-settings-list__info">
          <v-icon small color="text_secondary" class="mr-2">mdi-palette</v-icon>
          <span class="sc-settings-list__name">Cor de background</span>
        </div>
        <v-menu
          :close-on-content-click="false"
          transition="scale-transition"
          v-model="isColorPickerOpen"
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <div class="action-box color-box" v-bind="attrs" v-on="on">
              <div
                class="color-preview"
                :style="{ backgroundColor: backgroundColorPicker }"
              ></div>
              <span class="action-label">Fundo</span>
            </div>
          </template>
          <v-card class="color-picker-card">
            <v-card-text class="pa-3">
              <v-color-picker
                dot-size="20"
                mode="hexa"
                v-model="backgroundColorPicker"
                @input="onBackgroundColorChange"
                hide-inputs
                hide-mode-switch
              />
              <div
                class="color-preview-bar mt-3"
                :style="{ backgroundColor: backgroundColorHex }"
              >
                {{ backgroundColorHex }}
              </div>
              <div class="color-actions mt-3">
                <v-btn
                  outlined
                  small
                  class="reset-btn"
                  @click="backgroundColorPicker = '#FFFFFFFF'; onBackgroundColorChange('#FFFFFFFF')"
                >
                  <v-icon small left>mdi-refresh</v-icon>
                  Resetar
                </v-btn>
                <v-btn
                  color="primary"
                  small
                  class="save-btn"
                  @click="isColorPickerOpen = false"
                >
                  OK
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-menu>
      </div>
    </div>

    <!-- Save -->
    <div class="exibicao-footer">
      <v-btn
        color="primary"
        @click="updateDisplaySettings"
        :loading="loadingSave"
        class="modal-btn-confirm"
      >
        <v-icon small class="mr-1">mdi-check</v-icon>
        Salvar
      </v-btn>
    </div>
  </div>
</template>

<script>
import { apiClient } from "@/services/api";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "ConfigTabTelaExibicao",
  props: {
    codProducao: { type: Number, required: true },
    producao: { type: Object, required: true },
  },
  data() {
    return {
      localDarkMode: false,
      hasLocalDarkModeChanges: false,
      backgroundColorPicker: "#FFFFFF",
      backgroundColorChanged: false,
      hasChanges: false,
      isColorPickerOpen: false,
      loadingSave: false,
    };
  },
  created() {
    if (this.producao?.dsc_hex_background_chroma_noticias) {
      this.backgroundColorPicker = this.producao.dsc_hex_background_chroma_noticias;
    }
    this.localDarkMode = this.darkMode;
  },
  computed: {
    ...mapGetters({
      darkMode: "producoes/getterDarkMode",
    }),
    isDarkMode: {
      get() {
        return this.hasLocalDarkModeChanges ? this.localDarkMode : this.darkMode;
      },
      set(value) {
        this.localDarkMode = value;
        this.hasLocalDarkModeChanges = true;
        this.hasChanges = true;
      },
    },
    currentCardColor() {
      return this.isDarkMode ? "#2D2D2D" : "#FFFFFF";
    },
    backgroundColorHex() {
      if (!this.backgroundColorPicker) return "#FFFFFF";
      return typeof this.backgroundColorPicker === "object"
        ? this.backgroundColorPicker.hex || this.backgroundColorPicker.hexa
        : this.backgroundColorPicker;
    },
  },
  watch: {
    "$store.state.producoes.selectedProducao": {
      handler(newProducao, oldProducao) {
        if (!newProducao) return;
        const isNewProduction =
          !oldProducao || oldProducao.cod_producao !== newProducao.cod_producao;
        if (isNewProduction) {
          this.localDarkMode = this.darkMode;
          this.hasLocalDarkModeChanges = false;
          this.backgroundColorPicker =
            newProducao.dsc_hex_background_chroma_noticias || "#FFFFFF";
          this.backgroundColorChanged = false;
          this.hasChanges = false;
        }
      },
      deep: false,
      immediate: false,
    },
  },
  methods: {
    ...mapActions({
      updateDarkMode: "producoes/updateDarkMode",
    }),
    toggleDarkMode(value) {
      this.hasChanges = value !== this.darkMode || this.backgroundColorChanged;
    },
    onBackgroundColorChange(newColor) {
      this.backgroundColorPicker = newColor;
      this.backgroundColorChanged = true;
      this.hasChanges = true;
    },
    async updateDisplaySettings() {
      this.loadingSave = true;
      try {
        const promises = [];
        const currentBackgroundColor = this.backgroundColorPicker;

        if (this.hasLocalDarkModeChanges) {
          promises.push(
            this.updateDarkMode({
              cod_producao: this.codProducao,
              flg_dark_mode: this.localDarkMode,
            })
          );
        }

        if (this.backgroundColorChanged) {
          promises.push(
            apiClient.patch("/updateCorBackgroundChromaNoticias", {
              cod_producao: this.codProducao,
              flg_dark_mode: this.hasLocalDarkModeChanges
                ? this.localDarkMode
                : this.darkMode,
              dsc_hex_background_chroma_noticias: this.backgroundColorPicker,
            })
          );
        }

        await Promise.all(promises);

        this.$store.commit("producoes/updateSelectedProducao", {
          dsc_hex_background_chroma_noticias: currentBackgroundColor,
        });

        this.$toast.success("Configurações atualizadas com sucesso!");

        this.hasChanges = false;
        this.backgroundColorChanged = false;
        this.hasLocalDarkModeChanges = false;
      } catch (error) {
        this.$toast.error("Erro ao alterar configurações.");
      } finally {
        this.loadingSave = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* Settings list */


.color-box {
  border-style: solid;
}

.color-preview {
  width: 28px;
  height: 28px;
  border-radius: var(--sc-radius-sm);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.color-picker-card {
  border-radius: var(--sc-radius-lg) !important;
}

.color-preview-bar {
  height: 32px;
  border-radius: var(--sc-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.8rem;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.color-actions {
  display: flex;
  gap: 8px;

  .reset-btn {
    flex: 1;
    text-transform: none;
    letter-spacing: 0;
  }

  .save-btn {
    flex: 1;
    text-transform: none;
    letter-spacing: 0;
  }
}

/* Static color preview (read-only) */
.exibicao-color-preview-static {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  background: var(--sc-surface-tint);
  border: 1px solid var(--sc-border-emphasis);
  border-radius: var(--sc-radius-md);
}

.exibicao-color-hex {
  font-size: 0.8rem;
  color: var(--v-text_secondary-base);
  font-family: monospace;
}

/* Footer */
.exibicao-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--sc-border-subtle);
}

/* Switch overrides */
::v-deep .v-input--switch {
  margin: 0;
  padding: 0;
}

::v-deep .v-input--switch { margin: 0; padding: 0; }
</style>
